import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC GROUP" className="h-16 mx-auto" data-testid="forgot-pw-logo" />
          </Link>
        </div>
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold mb-2" data-testid="forgot-pw-title">
              {t('auth.resetPassword') || 'Reset Password'}
            </h1>
            <p className="text-white/50 text-sm">
              {t('auth.resetInstructions') || 'Enter your email and we\'ll send you a reset link'}
            </p>
          </div>

          {sent ? (
            <div className="text-center py-6" data-testid="forgot-pw-success">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-lg font-semibold mb-2">{t('auth.emailSent') || 'Email Sent!'}</h2>
              <p className="text-white/50 text-sm mb-6">
                {t('auth.checkInbox') || 'Check your inbox for the reset link. It expires in 30 minutes.'}
              </p>
              <Link to="/login" className="text-[#E8720C] hover:underline text-sm font-medium">
                {t('auth.backToLogin') || 'Back to Login'}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="forgot-pw-error">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  {t('auth.email') || 'Email'}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full input-glass pl-12"
                    placeholder="you@example.com"
                    required
                    data-testid="forgot-pw-email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-4 flex items-center justify-center gap-2"
                data-testid="forgot-pw-submit"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> {t('auth.sending') || 'Sending...'}</>
                ) : (
                  t('auth.sendResetLink') || 'Send Reset Link'
                )}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-white/40 hover:text-white/60 flex items-center justify-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {t('auth.backToLogin') || 'Back to Login'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
